import React, {
  useState, useCallback, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import * as utils from '../../../../../shared/utils';
import Markers from '../Markers';
import CommentRangeSlider from '../CommentRangeSlider';
import { resolveChapterEndTime } from '../helpers';
import Chapter from './Chapter';
import s from './Timeline.module.scss';

const Timeline = ({
  isMobile,
  hasDiff,
  currentTime,
  duration,
  bufferedEnd,
  chapters,
  timeCodeType,
  videoAiView,
  markers,
  showMarkersTrack,
  isShowHistoryPanel,
  isShowAiPanel,
  aiData,
  commentsRange = [0, 0],
  setCommentTimeRange = null,
  onToggleCommentRangeDragging = Function.prototype,
  isCheckedAttachTime,
  isVideoPaused,
  noProofingAccess,
  onClickTimeline,
  onSetCurrentTime,
}) => {
  const [hoverChapterIndex, setHoverChapterIndex] = useState(null);
  const [hoverChaptersProgress, setHoverChaptersProgress] = useState(null);
  const [hoverTrackPercent, setHoverTrackPercent] = useState(0);
  const [hoverTimeText, setHoverTimeText] = useState(null);
  const [hoveredTimePosition, setHoveredTimePosition] = useState(null); // 'left' | 'right' | null
  const $hoveredTime = useRef(null);
  const $videoTracks = useRef(null);
  const commentsRangeRef = useRef(commentsRange);

  useEffect(() => {
    commentsRangeRef.current = commentsRange;
  }, [commentsRange]);

  const hasChapters = Array.isArray(chapters) && chapters.length;
  const [commentStart, commentEnd] = commentsRange;
  const videoTracksWidth = $videoTracks.current?.offsetWidth || 0;
  const safeDuration = duration || 1;
  const marginLeft = (videoTracksWidth * commentStart) / safeDuration;
  const marginRight = (videoTracksWidth * (safeDuration - commentEnd)) / safeDuration;
  const isCommentTimeEqual = Math.floor(commentStart) === Math.floor(commentEnd);

  const handleTimeLineHover = useCallback((event) => {
    if (!duration) return;

    const $container = event.currentTarget.parentElement; // .videoTracks
    const rect = $container.getBoundingClientRect();
    const rawWidth = event.clientX - rect.left;
    const width = Math.max(0, Math.min(rect.width, rawWidth));
    const time = (duration * width) / rect.width;
    const timeText = utils.parseTime(time, { type: timeCodeType });

    if (hasChapters) {
      let localHoverChapterIndex = null;
      const localProgress = chapters.map((chap, index) => {
        const endTime = resolveChapterEndTime(chap, index, chapters, duration);

        if (time >= endTime) {
          return 1;
        }
        if (time >= chap.startTime && time <= endTime) {
          localHoverChapterIndex = index;
          if (endTime === chap.startTime) {
            return 0;
          }
          return (time - chap.startTime) / (endTime - chap.startTime);
        }
        return 0;
      });

      setHoverChapterIndex(localHoverChapterIndex);
      setHoverChaptersProgress(localProgress);
      const chapterText = localHoverChapterIndex != null ? chapters[localHoverChapterIndex].text : '';
      setHoverTimeText(chapterText ? `${timeText} ${chapterText}` : timeText);
    } else {
      setHoverTimeText(timeText);
    }
    const safePercent = Math.max(0, Math.min(100, (width / rect.width) * 100));
    setHoverTrackPercent(safePercent);

    window.requestAnimationFrame(() => {
      const tooltipWidth = $hoveredTime.current?.offsetWidth || 0;
      if (!tooltipWidth) {
        setHoveredTimePosition(null);
        return;
      }

      if (width < tooltipWidth / 2) {
        setHoveredTimePosition('left');
      } else if (rect.width - width < tooltipWidth / 2) {
        setHoveredTimePosition('right');
      } else {
        setHoveredTimePosition(null);
      }
    });
  }, [chapters, duration, hasChapters, timeCodeType]);

  const handleTimeLineLeave = useCallback(() => {
    setHoverChapterIndex(null);
    setHoverChaptersProgress(null);
    setHoverTrackPercent(0);
    setHoverTimeText(null);
    setHoveredTimePosition(null);
  }, []);

  const handleMouseDownCommentRangeEnd = useCallback((event) => {
    event.stopPropagation();
    if (!setCommentTimeRange || !$videoTracks.current || !duration) return;
    onToggleCommentRangeDragging(true);

    const mousemoveHandler = (e) => {
      const [start, end] = commentsRangeRef.current;
      const nextEnd = end + (e.movementX / $videoTracks.current.offsetWidth) * duration;

      if (nextEnd <= duration && nextEnd >= start) {
        onSetCurrentTime(nextEnd);
        setCommentTimeRange([start, nextEnd]);
      }
    };

    const mouseupHandler = () => {
      onToggleCommentRangeDragging(false);
      document.body.removeEventListener('mousemove', mousemoveHandler);
      document.body.removeEventListener('mouseup', mouseupHandler);
    };

    document.body.addEventListener('mousemove', mousemoveHandler);
    document.body.addEventListener('mouseup', mouseupHandler);
  }, [duration, onSetCurrentTime, onToggleCommentRangeDragging, setCommentTimeRange]);

  const handleMouseDownCommentRangeStart = useCallback((event) => {
    event.stopPropagation();
    if (!setCommentTimeRange || !$videoTracks.current || !duration) return;
    onToggleCommentRangeDragging(true);

    const mousemoveHandler = (e) => {
      const [start, end] = commentsRangeRef.current;
      const nextStart = start + (e.movementX / $videoTracks.current.offsetWidth) * duration;

      if (nextStart >= 0 && nextStart <= end) {
        onSetCurrentTime(nextStart);
        setCommentTimeRange([nextStart, end]);
      }
    };

    const mouseupHandler = () => {
      onToggleCommentRangeDragging(false);
      document.body.removeEventListener('mousemove', mousemoveHandler);
      document.body.removeEventListener('mouseup', mouseupHandler);
    };

    document.body.addEventListener('mousemove', mousemoveHandler);
    document.body.addEventListener('mouseup', mouseupHandler);
  }, [duration, onSetCurrentTime, onToggleCommentRangeDragging, setCommentTimeRange]);

  if (!duration) return null;

  return (
    <>
      <div
        className={cn(s.videoTracks, {
          [s.videoTracksBackgroundTransparent]: hasChapters,
        })}
        ref={$videoTracks}
      >
        <If condition={!isMobile || (hasDiff && isMobile)}>
          <div
            className={s.videoTracksPadding}
            onClick={onClickTimeline}
            onMouseMove={handleTimeLineHover}
            onMouseLeave={handleTimeLineLeave}
            role="presentation"
          >
            <If condition={hasChapters}>
              {chapters.map((chapter, index) => {
                const hoverProgress = Array.isArray(hoverChaptersProgress)
                  ? hoverChaptersProgress[index] || 0
                  : 0;
                const isHoveredChapter = hoverChapterIndex === index;
                const endTime = resolveChapterEndTime(chapter, index, chapters, duration);
                const timelineChapter = { ...chapter, endTime };

                const safeCurrentTime = currentTime || 0;
                const playbackProgress = (() => {
                  if (safeCurrentTime >= endTime) return 1;
                  if (safeCurrentTime <= chapter.startTime) return 0;
                  if (endTime === chapter.startTime) return 0;
                  return (safeCurrentTime - chapter.startTime) / (endTime - chapter.startTime);
                })();

                const safeBufferedEnd = bufferedEnd || 0;
                const bufferedProgress = (() => {
                  if (safeBufferedEnd >= endTime) return 1;
                  if (safeBufferedEnd <= chapter.startTime) return 0;
                  if (endTime === chapter.startTime) return 0;
                  return (safeBufferedEnd - chapter.startTime) / (endTime - chapter.startTime);
                })();

                return (
                  <Chapter
                    key={chapter.startTime}
                    chapter={timelineChapter}
                    duration={duration}
                    hoverProgress={hoverProgress}
                    isHoveredChapter={isHoveredChapter}
                    playbackProgress={playbackProgress}
                    bufferedProgress={bufferedProgress}
                  />
                );
              })}
            </If>

            <If condition={isShowHistoryPanel && isCheckedAttachTime && isVideoPaused}>
              <div
                className={s.timeRangeIndicator}
                style={{ left: marginLeft || 0 }}
              />
              <If condition={!isCommentTimeEqual}>
                <div
                  className={s.timeRangeIndicator}
                  style={{ right: marginRight || 0 }}
                />
              </If>
            </If>

            <If condition={hoverTimeText}>
              <div
                className={cn(s.hoveredTrack, hasChapters && s.hoveredTrackBackgroundTransparent, {
                  [s.hoveredTrackLeft]: hoveredTimePosition === 'left',
                  [s.hoveredTrackRight]: hoveredTimePosition === 'right',
                })}
                style={{ width: `${hoverTrackPercent || 50}%` }}
              >
                <span className={s.hoveredTime} ref={$hoveredTime}>
                  <span className={s.hoveredTimeText}>
                    {hoverTimeText}
                  </span>
                </span>
              </div>
            </If>
            <If condition={!hasChapters}>
              <div
                className={s.bufferedTrack}
                style={{
                  width: `${(100 * (bufferedEnd || 0)) / (duration || 1)}%`,
                }}
              />
              <div
                className={s.playbackTrack}
                style={{
                  width: `${(100 * currentTime) / (duration || 1)}%`,
                }}
              >
                <If condition={currentTime > 0 && currentTime < duration}>
                  <div
                    className={s.playPointMarkerNoChapters}
                  />
                </If>
              </div>
            </If>
          </div>
        </If>
        {!hasDiff && (
          <Markers
            duration={duration}
            showMarkersTrack={showMarkersTrack}
            isShowHistoryPanel={isShowHistoryPanel}
            markers={markers}
            isShowAiPanel={isShowAiPanel}
            videoAiView={videoAiView}
            aiData={aiData}
            onSetCurrentTime={onSetCurrentTime}
          />
        )}
      </div>
      {!hasDiff && (
        <CommentRangeSlider
          marginLeft={marginLeft || 0}
          marginRight={marginRight || 0}
          handleMouseDownCommentRangeEnd={handleMouseDownCommentRangeEnd}
          handleMouseDownCommentRangeStart={handleMouseDownCommentRangeStart}
          isHidden={!isCheckedAttachTime || !isVideoPaused || !isShowHistoryPanel}
          noProofingAccess={noProofingAccess}
        />
      )}
    </>
  );
};

Timeline.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  hasDiff: PropTypes.bool.isRequired,
  currentTime: PropTypes.number.isRequired,
  duration: PropTypes.number.isRequired,
  bufferedEnd: PropTypes.number,
  chapters: PropTypes.arrayOf(PropTypes.shape({
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number,
    text: PropTypes.string,
  })),
  timeCodeType: PropTypes.string.isRequired,
  videoAiView: PropTypes.string,
  markers: PropTypes.arrayOf(PropTypes.object).isRequired,
  showMarkersTrack: PropTypes.bool.isRequired,
  isShowHistoryPanel: PropTypes.bool.isRequired,
  isShowAiPanel: PropTypes.bool.isRequired,
  aiData: PropTypes.shape({
    actions: PropTypes.arrayOf(PropTypes.object),
    ocr: PropTypes.arrayOf(PropTypes.object),
    keywords: PropTypes.arrayOf(PropTypes.object),
  }),
  commentsRange: PropTypes.arrayOf(PropTypes.number),
  setCommentTimeRange: PropTypes.func,
  onToggleCommentRangeDragging: PropTypes.func,
  isCheckedAttachTime: PropTypes.bool.isRequired,
  isVideoPaused: PropTypes.bool,
  noProofingAccess: PropTypes.bool.isRequired,
  onClickTimeline: PropTypes.func.isRequired,
  onSetCurrentTime: PropTypes.func.isRequired,
};

Timeline.defaultProps = {
  bufferedEnd: 0,
  chapters: null,
  videoAiView: null,
  aiData: null,
  commentsRange: [0, 0],
  setCommentTimeRange: null,
  onToggleCommentRangeDragging: Function.prototype,
  isVideoPaused: false,
};

export default Timeline;
